import { InvoiceKind } from '@/context/services/invoice_kind/InvoiceKind.service';
import {
  InvoiceKindValues,
  TGetInvoiceKind,
} from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/services/type';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const INVOICE_SYSTEM_START_DATE = '2023/10/01';
const DEFAULT_VALIDITY_END_DATE = '9999/12/31';

type UnqualifiedMaster = {
  value: InvoiceKind;
  startAt: string | null;
  endAt: string | null;
};

/*
 * Please see the other periods.
 * https://www.nta.go.jp/taxes/shiraberu/zeimokubetsu/shohi/keigenzeiritsu/pdf/qa/01-15.pdf
 */
const UNQUALIFIED_MASTERS: UnqualifiedMaster[] = [
  {
    value: InvoiceKind.unqualified_eighty,
    startAt: null,
    endAt: '2026/10/01',
  },
  {
    value: InvoiceKind.unqualified_seventy,
    startAt: '2026/10/01',
    endAt: '2028/10/01',
  },
];

function isValidityPeriodValid(
  validityFromDate: Dayjs,
  validityToDate: Dayjs
): boolean {
  return validityFromDate.isValid() && validityToDate.isValid();
}

function isDealDateWithinValidityPeriod(
  dealDate: Dayjs,
  validityFromDate: Dayjs,
  validityToDate: Dayjs
): boolean {
  return (
    dealDate.isSameOrAfter(validityFromDate, 'day') &&
    dealDate.isSameOrBefore(validityToDate, 'day')
  );
}

function unqualifiedDefault(dealDate: Dayjs): InvoiceKind {
  const match = UNQUALIFIED_MASTERS.find(
    ({ startAt, endAt }) =>
      (startAt === null || dealDate.isSameOrAfter(dayjs(startAt), 'day')) &&
      (endAt === null || dealDate.isBefore(dayjs(endAt), 'day'))
  );
  // Fallback to the latest invoice kind (InvoiceKind.unqualifiedSeventy) for dealDate with value 2028/10/01 onwards
  // When enabling new invoice kind in the future, it's necessary to change the fallback value to the latest invoice kind
  return match?.value ?? InvoiceKind.unqualified_seventy;
}

// Don't import to react directly.
export const getInvoiceKind = ({
  dealDate,
  isZeroPer,
  tpmData,
  hasSpecialException,
  isDutyFree,
}: TGetInvoiceKind): InvoiceKindValues | '' => {
  // 日付が未設定の場合または免税事業者の場合は空
  // Return empty string if dealDate is undefined or isDutyFree is true
  if (!dealDate || !dealDate.isValid()) return '';

  const invoicingSystemEffectiveDate = dayjs(INVOICE_SYSTEM_START_DATE);
  // 取引日が2023/10/01より前の場合
  // If the dealDate is before 01/10/2023
  if (dealDate.isBefore(invoicingSystemEffectiveDate)) return '';

  // 税区分ゼロ税率対象の場合
  // return not_target if excises has isZeroPer = true
  if (isDutyFree || isZeroPer) return InvoiceKind.not_target;

  // デフォルトの有効期限終了日を設定
  // Set default expiry end date because default returning tpmData's validityEndDate is empty
  const defaultValidityTo =
    tpmData.validityEndDate || DEFAULT_VALIDITY_END_DATE;
  const validityStartDateObj = dayjs(tpmData.validityStartDate);
  const validityEndDateObj = dayjs(defaultValidityTo);

  const validityPeriodValid = isValidityPeriodValid(
    validityStartDateObj,
    validityEndDateObj
  );

  const dealDateWithinValidityPeriod = isDealDateWithinValidityPeriod(
    dealDate,
    validityStartDateObj,
    validityEndDateObj
  );

  if (
    (validityPeriodValid && dealDateWithinValidityPeriod) ||
    hasSpecialException
  ) {
    // deal date is within the invoice registration validity period or has special exception → qualified
    return InvoiceKind.qualified;
  } else {
    return unqualifiedDefault(dealDate);
  }
};
